/*
  2026-09-03 brand refresh, boards R2+R3 (canvas dfbe15b9). Values changed IN
  PLACE — no keys added or removed, so every import keeps working.
  R2: the Argon template's stray accents (purple linkHover, badge blues, cyan
      info, hot orange/pink alerts, teal→green gradients) retire into one
      accent (the teal) + chroma-matched semantics; gradientEnd === main
      everywhere, which flattens gradients where they are used.
  R3: chrome inks and grounds move into the teal family (Argon navy #172b4d →
      teal-ink #10302c; ground #f8f9fe → #f6f8f7; text slate warmed).
  Deliberately untouched: the gray ramp, black.*, droppedTrip/valid/invalid
  (mapPalette.js consumes these — map colors are data, not chrome), social
  brand colors, and primaryComplement.
*/
const themeColors = {
  white: {
    main: "#FFFFFF",
  },
  black: {
    light: "#12263F",
    main: "#000000",
  },
  transparent: {
    main: "transparent",
  },
  gray: {
    100: "#f6f9fc",
    200: "#e9ecef",
    300: "#dee2e6",
    400: "#ced4da",
    500: "#adb5bd",
    600: "#8898aa",
    700: "#525f7f",
    800: "#32325d",
    900: "#212529",
  },
  primary: {
    /*
      #00928B was the brand teal until 30 Aug. White text on it is 3.83:1 —
      under the 4.5:1 AA floor — so every filled primary button in the app was
      failing, including the home page's main call to action. axe never said so:
      it returns "incomplete" for these, not a violation, because MUI's ripple
      span overlaps the button and it cannot resolve the background.

      #017C76 is the smallest darkening that clears the bar with margin (5.07:1
      white-on-fill, and 5.07:1 as text on white). extraDark stays the hover, so
      the hover step survives.
    */
    main: "#017C76",
    light: "#b0d9d7",
    extraLight: '#e6fcfb',
    dark: "#015c58",
    extraDark: "#016e68",
    snackbar: "#2b9a92",
    badgeBg: "#e6f5f3",
    badgeBgHover: "#016e68",
    badge: "#017C76",
    inputCredit: "#017C76",
    gradientEnd: '#017C76',
    linkHover: '#016e68'
  },
  primaryComplement: {
    main: "#fff4f4",
    light: "#fff4f4",
    dark: "#570004",    
  },
  secondary: {
    main: "#f7fafc",
    snackbar: "#f8fbfc",
    badgeBgHover: "#cfe3e1",
    btnOutline: "#4a8f8a",
    btnActive: "#d5e6e4",
  },
  tertiary: {
    main: "#C23947",
    light: '#DC5361',
    dark: '#8F0614'
  },
  warning: {
    tableLight: "#fbe9d2",
    tableLightHover: "#f7dcb8",
    light: "#f0d3ae",
    extraLight: '#faf1e3',
    main: "#d9862b",
    snackbar: "#e09a4a",
    badgeBg: "#f8ead6",
    badgeBgHover: "#b56a1a",
    badge: "#c1741f",
    gradientEnd: '#d9862b'
  },
  error: {
    light: "#e3aab3",
    // For error TEXT. error.main is 3.77:1 on white, under the AA floor for the
    // small type it gets used at (helper messages, required markers). Same hue,
    // 5.6:1. error.main stays correct for borders and fills, judged at 3:1.
    text: "#c81e48",
    extraLight: '#f9e8ea',
    main: "#c6485a",
    snackbar: "#d16273",
    badgeBg: "#f6dade",
    badgeBgHover: "#a83646",
    badge: "#b23a4c",
    dialogNotification: "#c6485a",
    gradientEnd: '#c6485a'
  },
  info: {
    main: "#3193a6",
    light: '#7cc1cd',
    extraLight: '#e3f4f7',
    dark: '#0f7587',
    snackbar: "#4aa5b5",
    badgeBg: "#d9eef2",
    badgeBgHover: "#0f7587",
    badge: "#1f8496",
    gradientEnd: '#3193a6'
  },
  background: {
    default: "#f6f8f7",
  },
  text: {
    primary: "#4a5c59",
  },
  dark: {
    tableBorder: "#1e3a36",
    tableHeadColor: "#5d8f89",
    tableHeadBgColor: "#153531",
    main: "#10302c",
    light: '#7f9490',
    extraLight: '#e9f2f0',
    dark: "#081d1a",
    snackbar: "#2f4a46",
    badgeBg: "#2f6f69",
    badgeBgHover: "#06110f",
    gradientEnd: '#10302c'
  },
  success: {
    tableLightHover: "#c9e9dc",
    tableLight: "#b7e0d0",
    main: "#2c9c74",
    dark: '#00874d',
    light: '#a3d4c2',
    extraLight: '#e7f6f0',
    snackbar: "#43ab86",
    badgeBg: "#cdeadf",
    badgeBgHover: "#1f7d5c",
    badge: "#238a66",
    gradientEnd: '#2c9c74'
  },
  droppedTrip: {
    main: '#b20000',
    cluster: '#7f0000',
    highlight: '#ffa3a3'
  },
  badgeGray: {
    backgroundColor: '#f4f5f7',
    color: '#525f7f',
    borderColorHover: '#525f7f'
  },
  valid: {
    main: '#50C878'
  },
  invalid: {
    main: '#7f0000'
  },
  facebook: {
    main: "#3b5999",
    dark: "#2d4474",
  },
  twitter: {
    main: "#1da1f2",
    dark: "#0c85d0",
  },
  google: {
    main: "#dd4b39",
    dark: "#c23321",
  },
  instagram: {
    main: "#e4405f",
    dark: "#d31e40",
  },
  pinterest: {
    main: "#bd081c",
    dark: "#8c0615",
  },
  youtube: {
    main: "#cd201f",
    dark: "#a11918",
  },
  vimeo: {
    main: "#04A0F0",
    dark: "#037fbe",
  },
  slack: {
    main: "#3aaf85",
    dark: "#2d8968",
  },
  dribbble: {
    main: "#ea4c89",
    dark: "#e51e6b",
  },
  github: {
    main: "#222222",
    dark: "#090909",
  },
};

export default themeColors;
